/**
 * Utility functions for converting SVG elements to image formats (WebP/JPEG)
 * Used for exporting tactics visualizations as images
 */

/**
 * Check if browser supports WebP format
 */
export function supportsWebP(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
}

/**
 * Convert SVG element to canvas
 */
async function svgToCanvas(svgElement: SVGSVGElement, width?: number, height?: number): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    try {
      // Clone the SVG to avoid modifying the original
      const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
      
      // Get the actual bounding box of all content in the SVG
      const bbox = svgElement.getBBox();
      
      // Determine dimensions:
      // 1. Use provided dimensions if available
      // 2. Otherwise use bounding box dimensions (includes all content)
      // 3. Fallback to viewBox dimensions
      // 4. Final fallback to default dimensions
      let svgWidth: number;
      let svgHeight: number;
      
      if (width && height) {
        svgWidth = width;
        svgHeight = height;
      } else {
        // Use bounding box if it has valid dimensions, otherwise use viewBox or defaults
        const bboxWidth = bbox.width > 0 ? bbox.width : 0;
        const bboxHeight = bbox.height > 0 ? bbox.height : 0;
        const viewBoxWidth = svgElement.viewBox.baseVal.width || 0;
        const viewBoxHeight = svgElement.viewBox.baseVal.height || 0;
        
        svgWidth = bboxWidth || viewBoxWidth || svgElement.clientWidth || 1050;
        svgHeight = bboxHeight || viewBoxHeight || svgElement.clientHeight || 680;
      }
      
      // Preserve the original viewBox if it exists, otherwise set it based on dimensions
      const originalViewBox = svgElement.viewBox.baseVal;
      if (originalViewBox.width > 0 && originalViewBox.height > 0) {
        // Keep original viewBox to preserve aspect ratio and content positioning
        clonedSvg.setAttribute('viewBox', `${originalViewBox.x} ${originalViewBox.y} ${originalViewBox.width} ${originalViewBox.height}`);
      } else {
        // Set viewBox based on bounding box or dimensions
        if (bbox.width > 0 && bbox.height > 0) {
          clonedSvg.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
        } else {
          clonedSvg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
        }
      }
      
      // Set explicit width and height attributes
      clonedSvg.setAttribute('width', String(svgWidth));
      clonedSvg.setAttribute('height', String(svgHeight));
      
      // Ensure preserveAspectRatio is set to maintain aspect ratio
      if (!clonedSvg.getAttribute('preserveAspectRatio')) {
        clonedSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      }
      
      // Serialize SVG to string
      const svgData = new XMLSerializer().serializeToString(clonedSvg);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      
      // Create image element
      const img = new Image();
      
      img.onload = () => {
        // Create canvas with the target dimensions
        const canvas = document.createElement('canvas');
        canvas.width = svgWidth;
        canvas.height = svgHeight;
        
        // Draw image on canvas
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(svgUrl);
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Fill white background (for better image quality and consistency)
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw SVG image at full size
        ctx.drawImage(img, 0, 0, svgWidth, svgHeight);
        
        // Cleanup
        URL.revokeObjectURL(svgUrl);
        
        resolve(canvas);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        reject(new Error('Failed to load SVG image'));
      };
      
      img.src = svgUrl;
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Convert canvas to blob (WebP or JPEG)
 */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  format: 'webp' | 'jpeg',
  quality: number = 0.8
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const mimeType = format === 'webp' ? 'image/webp' : 'image/jpeg';
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error(`Failed to convert canvas to ${format}`));
        }
      },
      mimeType,
      quality
    );
  });
}

/**
 * Convert SVG element to image blob
 * 
 * @param svgElement - The SVG element to convert
 * @param options - Conversion options
 * @returns Promise resolving to image blob and format used
 */
export async function svgToImageBlob(
  svgElement: SVGSVGElement,
  options: {
    format?: 'webp' | 'jpeg';
    quality?: number;
    width?: number;
    height?: number;
  } = {}
): Promise<{ blob: Blob; format: 'webp' | 'jpeg'; sizeBytes: number }> {
  const {
    format: preferredFormat,
    quality = 0.75, // Default quality for ~50KB target
    width,
    height,
  } = options;

  // Determine format (prefer WebP if supported, fallback to JPEG)
  let format: 'webp' | 'jpeg';
  if (preferredFormat) {
    format = preferredFormat;
  } else {
    format = supportsWebP() ? 'webp' : 'jpeg';
  }

  try {
    // Convert SVG to canvas
    const canvas = await svgToCanvas(svgElement, width, height);
    
    // Convert canvas to blob
    const blob = await canvasToBlob(canvas, format, quality);
    
    return {
      blob,
      format,
      sizeBytes: blob.size,
    };
  } catch (error) {
    // If WebP fails, fallback to JPEG
    if (format === 'webp') {
      console.warn('WebP conversion failed, falling back to JPEG:', error);
      return svgToImageBlob(svgElement, { ...options, format: 'jpeg', quality });
    }
    throw error;
  }
}

/**
 * Convert SVG element to data URL (for preview/download)
 */
export async function svgToDataUrl(
  svgElement: SVGSVGElement,
  format: 'webp' | 'jpeg' = 'webp',
  quality: number = 0.8
): Promise<string> {
  const canvas = await svgToCanvas(svgElement);
  const mimeType = format === 'webp' ? 'image/webp' : 'image/jpeg';
  return canvas.toDataURL(mimeType, quality);
}

/**
 * Find SVG element in a container (searches for <svg> tag)
 */
export function findSVGElement(container: HTMLElement | null): SVGSVGElement | null {
  if (!container) return null;
  
  // Try direct query
  const svg = container.querySelector('svg');
  if (svg) return svg;
  
  // Try finding in children recursively
  const allSvgs = container.getElementsByTagName('svg');
  return allSvgs.length > 0 ? allSvgs[0] : null;
}

